import { useFormik } from "formik";
import { useEffect, useState } from "react";
import ModalComponent from "..";
import { useSendEmailBlastMutation } from "../../../redux/features/event/event.api";
import {
  CreatedEvent,
  GetEventTicketsBreakdownResponse,
  SendEmailBlast,
} from "../../../redux/features/event/event.types";
import { ModalCompProps } from "../../../utils/types";
import { SendEmailBlastSchema } from "../../../utils/validations";
import { Button } from "../../buttons/button";
import { ManualDropdown } from "../../dropdown/manualDropdown";
import { CaretDown } from "../../icon/downcaret";
import { Icon } from "../../icon/icon";
import { Checkbox } from "../../inputs/checkbox";
import { TextInput } from "../../inputs/textInput";
import { TabSelect } from "../../tabNav/tabSelect";
import { MiniHeader } from "../../typography/miniHeader";

const delimiter = ",";

export const SendEmailBlastModal = ({
  open,
  handleClose,
  event,
  onSuccess,
  ticketPurchaseBreakdown,
  totalGuests,
}: ModalCompProps & {
  event: CreatedEvent;
  onSuccess: () => void;
  ticketPurchaseBreakdown?: GetEventTicketsBreakdownResponse;
  totalGuests?: number;
}) => {
  const [recipientOptions, setRecipientOptions] = useState<
    {
      text: string;
      id: string;
      selected: boolean;
    }[]
  >([]);

  const [sendEmailBlast, { isLoading }] = useSendEmailBlastMutation();

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleBlur,
    handleChange,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      recipients: "",
      subject: "",
      message: "",
    },
    validationSchema: SendEmailBlastSchema,
    onSubmit: async (vals) => {
      const payload: SendEmailBlast = {
        subject: vals.subject || `New message in ${event.name}`,
        body: vals.message,
        recipients:
          vals.recipients === "all"
            ? vals.recipients
            : { ticketIds: vals.recipients.split(delimiter) },
        sendType: "now",
      };

      const resp = await sendEmailBlast({
        eventId: event._id,
        payload,
      });

      if (resp?.data?.status === "success") {
        handleClose();
        resetForm();
        onSuccess();
      }
    },
  });

  useEffect(() => {
    if (open) {
      setRecipientOptions([
        {
          text: `All Guests (${totalGuests})`,
          id: "all",
          selected: values.recipients === "all",
        },
        ...(ticketPurchaseBreakdown?.data?.map((tp) => {
          return {
            text: `${tp.ticketName} (${tp.totalQuantityPurchased})`,
            id: tp.ticketId,
            selected:
              values.recipients.split(delimiter).includes(tp.ticketId) ||
              values.recipients === "all",
          };
        }) ?? []),
      ]);
    }
  }, [open, ticketPurchaseBreakdown, totalGuests, values]);

  return (
    <ModalComponent
      open={open}
      handleClose={() => {
        handleClose();
        resetForm();
      }}
      className=""
      dialogClassName=""
      closeOnOverlayClick
      showHeader={false}
      title={undefined}
      subtitle={undefined}
      showBackIcon={false}
      mobileAnchorBottom
      showCloseIcon={false}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <Icon
            svg={"mails"}
            width={42}
            height={42}
            className="rounded-xl border border-grey-100 p-[11px]"
          />

          <button
            type="button"
            className="w-fit"
            onClick={() => {
              handleClose();
              resetForm();
            }}
          >
            <Icon svg={"close-icon"} width={24} height={24} />
          </button>
        </div>

        <div>
          <MiniHeader
            header={`Send Email Blast`}
            subheader="Guests would receive your email blasts once you send it."
          />
        </div>

        <ManualDropdown
          menu={({ close }) => {
            return (
              <div className="flex max-h-[208px] w-full flex-col overflow-y-scroll rounded-lg border border-grey-200 bg-white">
                {recipientOptions.map((option, idx) => {
                  return (
                    <Checkbox
                      key={`option-${idx}-${option.id}`}
                      checkStatus={option.selected}
                      handleChange={(checked) => {
                        const selected = recipientOptions
                          .filter((o) => o.selected)
                          .map((o) => o.id);

                        if (checked) {
                          if (option.id === "all") {
                            setFieldValue("recipients", "all");
                            close();
                          } else {
                            setFieldValue(
                              "recipients",
                              [
                                ...selected.filter((i) => i !== "all"),
                                option.id,
                              ].join(delimiter),
                            );
                          }
                        } else {
                          if (option.id === "all") {
                            setFieldValue("recipients", "");
                          } else {
                            if (selected.includes("all")) {
                              setFieldValue(
                                "recipients",
                                [
                                  ...selected.filter((i) => i === option.id),
                                ].join(delimiter),
                              );
                            } else {
                              setFieldValue(
                                "recipients",
                                [
                                  ...selected.filter(
                                    (i) => i !== option.id && i !== "all",
                                  ),
                                ].join(delimiter),
                              );
                            }
                          }
                        }
                      }}
                      text={option.text}
                      textPosition="right"
                      className="w-full border-b border-grey-200 px-4 py-[18px] last:border-b-0"
                    />
                  );
                })}
              </div>
            );
          }}
        >
          {() => {
            return (
              <div className="w-full text-left">
                <TextInput
                  type="text"
                  value={values.recipients}
                  hasTitle
                  title="Recipients"
                  placeholder="Select recipients"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  name="name"
                  readOnly={true}
                  errors={
                    touched.recipients && errors.recipients
                      ? errors.recipients
                      : undefined
                  }
                  customIcon={<CaretDown />}
                  customIconPosition="right"
                  customChild={
                    values.recipients ? (
                      <TabSelect
                        tabs={recipientOptions
                          .filter((o) => o.selected)
                          .map((option) => {
                            return {
                              title: option.text,
                              to: "",
                              className:
                                recipientOptions
                                  .filter((o) => o.selected)
                                  .map((o) => o.id)
                                  .includes("all") && option.id !== "all"
                                  ? "!hidden"
                                  : "",
                              onClick: () => {},
                              onRemove: () => {
                                const selected = recipientOptions
                                  .filter((o) => o.selected)
                                  .map((o) => o.id);

                                if (option.id === "all") {
                                  setFieldValue("recipients", "");
                                } else {
                                  setFieldValue(
                                    "recipients",
                                    [
                                      ...selected.filter(
                                        (i) => i !== option.id,
                                      ),
                                    ].join(delimiter),
                                  );
                                }
                              },
                            };
                          })}
                        className=""
                        tabClassName="!bg-grey-100 !border-0"
                      />
                    ) : undefined
                  }
                />
              </div>
            );
          }}
        </ManualDropdown>

        <TextInput
          type="text"
          value={values.subject}
          hasTitle
          title="Subject (Optional)"
          placeholder={`New message in ${event.name}`}
          onBlur={handleBlur}
          onChange={handleChange}
          name="subject"
          readOnly={false}
          errors={
            touched.subject && errors.subject ? errors.subject : undefined
          }
        />

        <TextInput
          type="textarea"
          value={values.message}
          hasTitle
          title="Message"
          placeholder="Enter message for your guests"
          onBlur={handleBlur}
          onChange={handleChange}
          name="message"
          readOnly={false}
          errors={
            touched.message && errors.message ? errors.message : undefined
          }
          rows={4}
        />

        <div className="flex w-full items-center justify-between gap-2">
          <button
            type="button"
            className="font-bold text-primary"
            disabled={isLoading}
            onClick={async () => {
              const payload: SendEmailBlast = {
                subject: values.subject || `New message in ${event.name}`,
                body: values.message,
                recipients: undefined,
                sendType: "test",
              };

              await sendEmailBlast({
                eventId: event._id,
                payload,
              });
            }}
          >
            Send a Test Mail
          </button>

          <Button
            text={"Send"}
            type="submit"
            onClick={() => {}}
            className="max-w-fit"
            loading={isLoading}
            disabled={isLoading}
            variation={"primary"}
          />
        </div>
      </form>
    </ModalComponent>
  );
};
